/**
 * 认证状态管理 - 使用Zustand
 * Authentication State Management - Using Zustand
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 用户信息接口
export interface UserInfo {
  id: string;
  username: string;
  realName: string;
  phone: string;
  email?: string;
  roles: string[];
  permissions: string[];  // 移除可选标记
  permissionsUpdatedAt?: number;  // 权限更新时间（服务端时间戳毫秒）
  lastLoginAt?: string;
  createdAt: string;
}

// 认证状态接口
interface AuthState {
  // 状态
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  
  // 操作方法
  login: (credentials: { username: string; password: string }) => Promise<{ success: boolean; message: string }>;
  register: (userData: {
    username: string;
    password: string;
    confirmPassword: string;
    realName: string;
    phone: string;
    email?: string;
  }) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUserInfo: (userInfo: Partial<UserInfo>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  checkPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isPermissionsExpired: () => boolean;  // 新增：检查权限是否过期
  refreshUserPermissions: () => Promise<void>;  // 新增：刷新用户权限
}

// API基础URL - 使用与api.ts相同的配置
// API Base URL - Use the same configuration as api.ts
// 在生产环境中，如果没有设置环境变量，使用相对路径 /api
// In production, if no environment variable is set, use relative path /api
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
let permissionsRefreshPromise: Promise<void> | null = null;

// 创建认证store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,

      // 登录方法
      login: async (credentials) => {
        set({ loading: true, error: null });
        
        try {
          console.log(`[AuthStore] 开始登录: ${credentials.username}`);
          
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
          });

          const data = await response.json();
          
          // 调试日志 - 查看后端返回的完整数据
          console.log(`[AuthStore] 登录响应:`, data);
          console.log(`[AuthStore] 用户数据:`, data.user);
          console.log(`[AuthStore] 用户权限:`, data.user?.permissions);

          if (data.success) {
            // 确保 permissions 数组存在
            const userData = {
              ...data.user,
              permissions: data.user?.permissions || [],
              permissionsUpdatedAt: data.user?.permissionsUpdatedAt
                ? new Date(data.user.permissionsUpdatedAt).getTime()
                : Date.now()
            };
            
            console.log(`[AuthStore] 保存的用户数据:`, userData);
            
            set({
              isAuthenticated: true,
              user: userData,
              token: data.token,
              loading: false,
              error: null,
            });
            
            return { success: true, message: data.message };
          } else {
            set({
              loading: false,
              error: data.message,
            });
            
            return { success: false, message: data.message };
          }
        } catch (error) {
          const errorMessage = '网络错误，请检查连接';
          set({
            loading: false,
            error: errorMessage,
          });
          
          return { success: false, message: errorMessage };
        }
      },

      // 注册方法
      register: async (userData) => {
        set({ loading: true, error: null });
        
        try {
          const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
          });

          const data = await response.json();

          if (data.success) {
            set({
              isAuthenticated: true,
              user: data.user,
              token: data.token,
              loading: false,
              error: null,
            });
            
            return { success: true, message: data.message };
          } else {
            set({
              loading: false,
              error: data.message,
            });
            
            return { success: false, message: data.message };
          }
        } catch (error) {
          const errorMessage = '网络错误，请检查连接';
          set({
            loading: false,
            error: errorMessage,
          });
          
          return { success: false, message: errorMessage };
        }
      },

      // 注销方法
      logout: () => {
        const { user, token } = get();
        
        // 发送注销请求到后端
        if (user && token) {
          fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              userId: user.id,
              username: user.username,
            }),
          }).catch(error => {
            console.error('注销请求失败:', error);
          });
        }

        // 清除本地状态
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
          error: null,
        });
      },

      // 刷新令牌
      refreshToken: async () => {
        const { token } = get();
        
        if (!token) {
          return false;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken: token }),
          });

          const data = await response.json();

          if (data.success) {
            set({
              token: data.token,
            });
            return true;
          } else {
            // 刷新失败，清除认证状态
            set({
              isAuthenticated: false,
              user: null,
              token: null,
            });
            return false;
          }
        } catch (error) {
          console.error('刷新令牌失败:', error);
          set({
            isAuthenticated: false,
            user: null,
            token: null,
          });
          return false;
        }
      },

      // 更新用户信息
      updateUserInfo: (userInfo) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...userInfo },
          });
        }
      },

      // 清除错误
      clearError: () => {
        set({ error: null });
      },

      // 设置加载状态
      setLoading: (loading) => {
        set({ loading });
      },

      // 检查权限 - 纯判断版本
      // Check permission - pure check only
      checkPermission: (permission) => {
        const { user } = get();
        
        if (!user) {
          return false;
        }
        
        if (!user.permissions || !Array.isArray(user.permissions)) {
          return false;
        }
        
      // 支持 edit/update 别名匹配（语义等效：修改现有资源）
      const permParts = permission.split(':');
      const resource = permParts[0];
      const action = permParts[1] || '';
      const aliasesToCheck = (action === 'edit' || action === 'update')
        ? [permission, `${resource}:edit`, `${resource}:update`]
        : [permission];
      return aliasesToCheck.some(p => user.permissions.includes(p));
      },

      // 检查角色
      hasRole: (role) => {
        const { user } = get();
        if (!user || !user.roles) {
          return false;
        }
        return user.roles.includes(role);
      },
      
      // 检查权限是否过期（1小时）
      // Check if permissions are expired (1 hour)
      isPermissionsExpired: () => {
        const { user } = get();
        if (!user || !user.permissionsUpdatedAt) {
          return true;  // 没有更新时间，视为过期
        }
        
        const ONE_HOUR = 60 * 60 * 1000;
        const isExpired = Date.now() - user.permissionsUpdatedAt > ONE_HOUR;
        
        if (isExpired) {
          console.log('[AuthStore] 权限已过期，需要刷新');
        }
        
        return isExpired;
      },
      
      // 刷新用户权限
      // Refresh user permissions
      refreshUserPermissions: async () => {
        if (permissionsRefreshPromise) {
          return permissionsRefreshPromise;
        }

        permissionsRefreshPromise = (async () => {
          const { token, user, logout } = get();
          
          if (!token || !user) {
            console.log('[AuthStore] 无法刷新权限：未登录');
            return;
          }
          
          try {
            console.log('[AuthStore] 开始刷新用户权限...');
            
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.status === 401) {
              console.warn('[AuthStore] 权限刷新返回 401，执行退出登录');
              logout();
              return;
            }
            
            const data = await response.json();
            
            if (response.ok && data.success && data.user) {
              console.log('[AuthStore] 权限刷新成功');

              const latestUser = get().user;
              if (!latestUser) {
                return;
              }
              
              // 更新用户信息和权限
              set({
                user: {
                  ...latestUser,
                  ...data.user,
                  permissions: data.user.permissions || [],
                  permissionsUpdatedAt: data.user.permissionsUpdatedAt
                    ? new Date(data.user.permissionsUpdatedAt).getTime()
                    : Date.now()
                }
              });
            } else {
              console.error('[AuthStore] 权限刷新失败:', data.message);
            }
          } catch (error) {
            console.error('[AuthStore] 刷新权限时发生错误:', error);
          } finally {
            permissionsRefreshPromise = null;
          }
        })();

        return permissionsRefreshPromise;
      },
    }),
    {
      name: 'auth-storage', // 本地存储的key
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
      }),
    }
  )
);
